import { useCallback, useEffect, useState } from "react";
import api, { euro, fmtDate, todayISO, can } from "../lib/api";
import { useQuick } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function Expenses() {
  const { openModal } = useQuick();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [category, setCategory] = useState("");
  const [cats, setCats] = useState([]);

  const load = useCallback(async () => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    if (category) params.category = category;
    const { data } = await api.get("/expenses", { params });
    setExpenses(data);
  }, [from, to, category]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings").then(({ data }) => setCats(data.expense_categories || [])); }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ll:data-changed", h);
    return () => window.removeEventListener("ll:data-changed", h);
  }, [load]);

  const del = async (e2) => {
    if (!window.confirm("Eliminare questa spesa?")) return;
    await api.delete(`/expenses/${e2.id}`);
    toast.success("Spesa eliminata");
    load();
  };

  const total = expenses.reduce((s, x) => s + (x.amount || 0), 0);

  const exportCSV = () => {
    const rows = [["Data", "Categoria", "Descrizione", "Fornitore", "Metodo", "Importo", "Note"]];
    expenses.forEach((x) => rows.push([x.date, x.category, x.description, x.supplier, x.method, x.amount, x.notes || ""]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = `spese_${todayISO()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="expenses-page">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-2xl font-bold">Spese</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input data-testid="expenses-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" />
          <input data-testid="expenses-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" />
          <select data-testid="expenses-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white">
            <option value="">Tutte le categorie</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button data-testid="expenses-export" onClick={exportCSV} className="p-2.5 rounded-xl bg-white border border-slate-200" title="Esporta Excel"><FileSpreadsheet size={15} /></button>
          <button data-testid="expenses-new" onClick={() => openModal("expense")} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium"><Plus size={15} /> Spesa</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center justify-between" data-testid="expenses-total">
        <p className="text-sm text-slate-500">{expenses.length} spese nel periodo</p>
        <p className="font-heading text-2xl font-bold text-amber-600">{euro(total)}</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
              <th className="py-3 px-4">Data</th><th className="px-3">Categoria</th><th className="px-3">Descrizione</th><th className="px-3 hidden md:table-cell">Fornitore</th><th className="px-3">Metodo</th><th className="px-3">Importo</th><th className="px-3"></th>
            </tr></thead>
            <tbody>
              {expenses.map((x) => (
                <tr key={x.id} data-testid={`expense-row-${x.id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="py-3 px-4 whitespace-nowrap">{fmtDate(x.date)}</td>
                  <td className="px-3"><span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{x.category}</span></td>
                  <td className="px-3">{x.description}</td>
                  <td className="px-3 text-slate-500 hidden md:table-cell">{x.supplier || "—"}</td>
                  <td className="px-3 capitalize text-slate-600">{x.method}</td>
                  <td className="px-3 font-semibold text-amber-600">{euro(x.amount)}</td>
                  <td className="px-3">{can(user, "spese", "delete") && <button data-testid={`expense-delete-${x.id}`} onClick={() => del(x)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>}</td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-slate-400" data-testid="expenses-empty">Nessuna spesa nel periodo</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
